/**
 * Locale-aware route aliases.
 *
 * Canonical EN slug → localized slug per supported locale.
 * The canonical EN path is always mounted under every `/:locale/*`, so
 * existing links keep working. Aliases are *additive* — they bind the
 * localized slug to the same page component as the canonical EN slug.
 */
import type { SupportedLanguage } from "./index";

export const ROUTE_ALIASES = {
  catalog: { fr: "bootcamps", es: "bootcamps", pt: "bootcamps" },
  pricing: { fr: "tarifs", es: "precios", pt: "precos" },
  "case-studies": {
    fr: "etudes-de-cas",
    es: "estudios-de-caso",
    pt: "estudos-de-caso",
  },
  downloads: { fr: "telechargements", es: "descargas", pt: "downloads" },
  careers: { fr: "carrieres", es: "empleos", pt: "carreiras" },
  about: { fr: "a-propos", es: "acerca", pt: "sobre" },
  login: { fr: "connexion", es: "iniciar-sesion", pt: "entrar" },
  signup: { fr: "inscription", es: "registro", pt: "cadastro" },
  "business-validator": { fr: "validateur-business", es: "validador-negocio", pt: "validador-negocio" },
} as const;

export type CanonicalRoute = keyof typeof ROUTE_ALIASES;

/** All localized aliases (de-duplicated) for parallel route registration. */
export const ALL_ALIASES: ReadonlyArray<{ canonical: CanonicalRoute; alias: string }> =
  (Object.entries(ROUTE_ALIASES) as [CanonicalRoute, Record<string, string>][])
    .flatMap(([canonical, map]) =>
      Array.from(new Set(Object.values(map))).map((alias) => ({ canonical, alias })),
    )
    .filter(({ canonical, alias }) => alias !== canonical);

/** Convenience map by canonical key. */
export function aliasFor(canonical: CanonicalRoute, locale: SupportedLanguage): string {
  const map = ROUTE_ALIASES[canonical] as Partial<Record<SupportedLanguage, string>>;
  return map?.[locale] ?? canonical;
}